import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, filter, finalize, Observable, of, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  RouteSummaryResponse,
  TrackingNumberSearchResponse,
  UploadFileResponse,
} from 'src/app/core/models/operations';
import {
  RouteDetailsResponse,
  ScanCodesResponse,
} from 'src/app/core/models/operations/operations-route-details.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/environments/environment';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class SummaryService {
  constructor(
    private http: HttpClient,
    private commonUtils: CommonUtils,
    private errorHandler: ErrorhandleService,
  ) {}

  baseUrl = environment.apiUrl.concat('operations-management/');

  /**
   * Get Route summary for Operations
   *
   * @param {string} locationCode
   * @param {string} planDate
   * @return {*}  {Observable<RouteSummaryResponse>}
   * @memberof SummaryService
   */
  getRouteSummary(
    locationCode: string,
    planDate: string, // MM/dd/YYYY
  ): Observable<RouteSummaryResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<RouteSummaryResponse>(this.baseUrl.concat('routesummary'), {
        params: { locationCode: locationCode, planDate: planDate },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<RouteSummaryResponse>(
            error,
            error.status,
            'Operations Service: Get Route Summary',
            BANNER_TYPE.ROUTE_SUMMARY_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * To get route details
   *
   * @param {string} locationCode
   * @param {string} dateFrom
   * @param {string} dateTo
   * @param {string} loop
   * @return {*}  {Observable<RouteDetailsResponse>}
   * @memberof SummaryService
   */
  getRouteDetails(
    locationCode: string,
    dateFrom: string,
    dateTo: string,
    loop: string,
  ): Observable<RouteDetailsResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<RouteDetailsResponse>(this.baseUrl.concat('routedetails'), {
        params: {
          locationCode: locationCode,
          dateFrom: dateFrom,
          dateTo: dateTo,
          loop: loop,
        },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<RouteDetailsResponse>(
            error,
            error.status,
            'Operations Service: Get Route Details',
            BANNER_TYPE.TABLE_DATA_ERROR,
          ),
        ),
      );
  }

  /**
   * To get available scan codes
   *
   * @return {*}  {Observable<ScanCodesResponse>}
   * @memberof SummaryService
   */
  getScanCodes(): Observable<ScanCodesResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http.get<ScanCodesResponse>(this.baseUrl + 'delscancodes').pipe(
      take(1),
      catchError((response: HttpErrorResponse) => {
        return this.errorHandler.handleError<ScanCodesResponse>(
          response,
          response.status,
          'Summary Service: Scan Codes',
          BANNER_TYPE.API_400_ERROR,
          response as unknown as ScanCodesResponse,
        );
      }),
      filter((value) => !!value),
      finalize(() => {
        this.commonUtils.updateLoadingModal(false);
      }),
    );
  }

  uploadFile(file: FormData): Observable<UploadFileResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http.post(environment.fileUploadUrl, file).pipe(
      take(1),
      catchError((error) => {
        this.errorHandler.handleError<UploadFileResponse>(
          error,
          error.status,
          'Summary Service: File Upload',
          undefined,
          undefined,
          'fileupload',
        );
        return of(error);
      }),
      finalize(() => {}),
    );
  }

  searchByTrackNum(
    trackingNumber: string,
  ): Observable<TrackingNumberSearchResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get(
        this.baseUrl.concat(
          `searchbytracknum?trackingNumber=${trackingNumber}`,
        ),
      )
      .pipe(
        take(1),
        catchError((error) => {
          this.errorHandler.handleError<UploadFileResponse>(
            error,
            error.status,
            'Summary Service: Search by Tracking Number',
          );
          return of(error);
        }),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
